import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, Clock, Share2, Facebook, Twitter, Linkedin } from 'lucide-react';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import Layout from '../components/layout/Layout';
import NewsCard from '../components/news/NewsCard';
import NewsletterForm from '../components/common/NewsletterForm';
import { newsAPI } from '../lib/api';
import { formatDate, getCategoryColor, resolveImageUrl } from '../lib/utils';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

// Format date with time
const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-PT', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const ArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedNews, setRelatedNews] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadArticle = useCallback(async () => {
    setLoading(true);
    try {
      const response = await newsAPI.getBySlug(slug);
      setArticle(response.data);
      
      // Load related news
      const relatedResponse = await newsAPI.getAll({
        category: response.data.category,
        limit: 4,
      });
      setRelatedNews(relatedResponse.data.filter(n => n.slug !== slug).slice(0, 3));
    } catch (error) {
      console.error('Error loading article:', error);
      toast.error('Artigo não encontrado');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadArticle();
  }, [loadArticle]);

  const shareUrl = window.location.href;

  const handleShare = (platform) => {
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(article?.title || '')}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    };
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  if (!article) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-12 text-center">
          <h1 className="font-['Playfair_Display'] text-2xl font-bold text-[#1A1A1A] mb-4">
            Artigo não encontrado
          </h1>
          <Link to="/">
            <Button className="bg-[#0F5A34] hover:bg-[#0A3F24]">
              Voltar ao Início
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Article Header */}
      <section className="py-10 md:py-12 border-b border-[#D6D2C4] bg-[#E9E5D6]/40" data-testid="article-header">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex items-center gap-2 text-sm text-[#5C5C5C] font-['Inter'] mb-5">
            <Link to="/" className="hover:text-[#0F5A34]">Início</Link>
            <ChevronRight size={14} />
            <Link to={`/categoria/${encodeURIComponent(article.category)}`} className="hover:text-[#0F5A34]">
              {article.category}
            </Link>
            <ChevronRight size={14} />
            <span className="text-[#1A1A1A] line-clamp-1">{article.title}</span>
          </div>

          <Badge className={`${getCategoryColor(article.category)} text-white font-['Montserrat'] text-xs uppercase tracking-wider mb-5`}>
            {article.category}
          </Badge>

          <h1 className="font-['Playfair_Display'] text-3xl md:text-5xl lg:text-6xl font-bold text-[#1A1A1A] mb-5 leading-[1.1] tracking-tight">
            {article.title}
          </h1>

          {article.subtitle && (
            <p className="text-[#3F3F3F] font-['Inter'] text-lg md:text-2xl mb-6 leading-relaxed max-w-3xl">
              {article.subtitle}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-4 text-sm text-[#5C5C5C] font-['Inter']">
            <span className="flex items-center gap-2">
              <Clock size={16} />
              {formatDateTime(article.created_at)}
            </span>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-10 md:py-14" data-testid="article-content">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            {/* Main Content */}
            <article className="lg:col-span-8">
              {/* Featured Image */}
              {article.image_url && (
                <figure className="aspect-[16/9] rounded-sm overflow-hidden mb-10 shadow-sm">
                  <img
                    src={resolveImageUrl(article.image_url)}
                    alt={article.title}
                    loading="eager"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                </figure>
              )}
              
              {/* Article Body (HTML sanitized) */}
              <div
                className="article-content"
                data-testid="article-body"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(article.content || '', {
                    ALLOWED_TAGS: [
                      'p','br','div','span','strong','b','em','i','u','s','strike',
                      'a','img','ul','ol','li','blockquote','pre','code',
                      'h1','h2','h3','h4','h5','h6','hr','font',
                      'table','thead','tbody','tfoot','tr','th','td','caption','colgroup','col',
                      'figure','figcaption','section','header','footer','article','aside',
                      'small','mark','sup','sub','time',
                    ],
                    ALLOWED_ATTR: [
                      'style','class','href','title','target','rel',
                      'src','alt','width','height','face','size','color',
                      'colspan','rowspan','scope','datetime',
                    ],
                  }),
                }}
              />

              {/* Share Buttons */}
              <div className="border-t border-b border-[#D6D2C4] py-6 my-8">
                <div className="flex items-center gap-4">
                  <span className="font-['Montserrat'] text-sm font-medium text-[#5C5C5C]">
                    <Share2 size={16} className="inline mr-2" />
                    Compartilhar:
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShare('facebook')}
                    className="text-[#1877F2] border-[#1877F2] hover:bg-[#1877F2] hover:text-white"
                    data-testid="share-facebook"
                  >
                    <Facebook size={16} />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShare('twitter')}
                    className="text-[#1DA1F2] border-[#1DA1F2] hover:bg-[#1DA1F2] hover:text-white"
                    data-testid="share-twitter"
                  >
                    <Twitter size={16} />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShare('linkedin')}
                    className="text-[#0A66C2] border-[#0A66C2] hover:bg-[#0A66C2] hover:text-white"
                    data-testid="share-linkedin"
                  >
                    <Linkedin size={16} />
                  </Button>
                </div>
              </div>

              {/* Related News */}
              {relatedNews.length > 0 && (
                <div className="mt-12">
                  <h3 className="font-['Playfair_Display'] text-2xl font-bold text-[#1A1A1A] mb-6">
                    Notícias Relacionadas
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {relatedNews.map((news) => (
                      <NewsCard key={news.id} news={news} />
                    ))}
                  </div>
                </div>
              )}
            </article>

            {/* Sidebar */}
            <aside className="lg:col-span-4 space-y-6">
              <div className="sticky top-24">
                <NewsletterForm variant="sidebar" />
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <NewsletterForm />
    </Layout>
  );
};

export default ArticlePage;
